import { ColorInfo, Flag, PriceInfo } from '#constants/types/algolia';
import { NorceUserTypes } from '#constants/norceCodes';

type InfoType = {name: string, value: string};

/**
 * The datalayer built by backend
 * Should be same from algolia and cartapi
 */
export type ProductDataLayer = {
  item_id: string,
  item_name: string,
  item_variant: string,
  item_brand: string,
  item_category: string | null,
  item_category2: string | null,
  item_category3: string | null,
  price: string
}

export type CartApiPayload = {
  items: {
    lineNo?: number,
    partNo: string,
    quantity: number,
    info?: {
      value: string; // example Sara
      code: string; // example nametag_text1
    }[],
    isCrossSell?: boolean,
    priceListLocked?: boolean,
  }[]
}

export type CartApiItem = {
  appliedPromotions: AppliedPromotion[],
  brand: Brand,
  categories: Category[],
  colorInfo: ColorInfo | null,
  dataLayer: ProductDataLayer,
  flags: Flag[],
  image: string,
  infoTypes: InfoType[],
  lineNo: number,
  name: string,
  onHandValue: number,
  parentLineNo: null,
  partNo: string,
  priceInfo: PriceInfo,
  priceInfoTotal: PriceInfo,
  priceListLocked: boolean,
  promotionIdSeed?: string | null,
  quantity: number,
  statusId: number,
  stockStatus: {
    value: number,
    incomingValue: number,
    leadtimeDayCount?: number | null,
    estimatedDelivery?: string | null,
  },
  supplierStockStatus: {
    value: number,
    incomingValue: number,
    leadtimeDayCount?: number | null,
    estimatedDelivery?: string | null,
  },
  url: string,
  variantName: string,
  recommendedQty?: number,
  isRecommendedQtyFixed: boolean,
  isD2C?: boolean,
}

export type Brand = {
  code: string,
  description: string,
  name: string,
}

export type Category = {
  code: string,
  description: string,
  name: string,
  parentCode: string,
  parentName: string,
}

type CartApiSummary = {
  discount: string;
  discountDisplay: string;
  points: number;
  price: string;
  priceBeforeVat: string;
  priceBeforeDiscount: string,
  priceBeforeVatDisplay: string;
  priceBeforeVatAndDiscount?: string;
  priceBeforeDiscountDisplay?: string;
  priceBeforeVatAndDiscountDisplay?: string;
  priceDisplay: string;
  shipping: string;
  shippingBeforeVat: string;
  shippingBeforeVatDisplay: string;
  shippingDisplay: string;
  subtotal: string;
  subtotalBeforeVat: string;
  subtotalBeforeVatDisplay: string;
  subtotalDisplay: string;
  vat: string;
  vatDisplay: string;
  usedPoints: number;
  crossDockingShipping: string;
  crossDockingShippingDisplay: string;
  crossDockingShippingBeforeVat: string;
  crossDockingShippingBeforeVatDisplay: string;
  additionalFees?: CartApiSummaryAdditionalFee[];
}

export type CartApiSummaryAdditionalFee = {
  name: string;
  fee?: string;
  feeDisplay?: string;
  feeBeforeVat?: string;
  feeBeforeVatDisplay?: string;
}

export type CartApiResponse = {
  allowPointsPurchase?: boolean;
  appliedPromotions?: AppliedPromotion[],
  comment?: string;
  customerId?: number,
  id: string;
  norceBasketId?: string,
  infoTypes?: InfoType[],
  items: CartApiItem[],
  messages?: string[],
  paymentInfo?: { hasSelectedMethod: boolean; isInvoicePayment: boolean; },
  statusId?: number;
  summary: CartApiSummary,
  savedBasket?: SavedCart,
}

export type AppliedPromotion = {
  name: string;
  discountCode: string;
  appliedAmount: number;
  appliedAmountDisplay: '';
  appliedAmountIncVat: number;
  appliedAmountIncVatDisplay: string;
}

export type CheckoutAddressDetails = {
  companyName?: string | null,
  firstName: string | null,
  lastName: string | null,
  name: string | null,
  orgNo?: string | null,
  street: string | null,
  zip: string | null,
  city: string | null,
  country?: string | null,
  phone?: string | null,
  email?: string | null,
  toConsumer?: boolean | null,
  id?: number | null,
  state: string | null,
  region?: string | null,
}

export type CheckoutDeliveryMethod = {
  id:	string,
  name:	string,
  price:	string,
  priceDisplay:	string,
  cost?:	string,
  costDisplay?:	string,
  image:	string,
  selected:	boolean,
}

export type CheckoutPaymentMethodService = {
  id: number,
  name: string,
  description: string | null,
  imagePath: string | null,
  imageKey: string | null,
}

export type CheckoutPaymentMethod = {
  id:	number,
  name:	string,
  image:	string | null,
  selected:	boolean,
  description?: string,
  imageKey?: string | null,
  isForCompanyOnly?: boolean,
  isForPersonOnly?: boolean,
  partNo?: string | null,
  price?: string,
  priceDisplay?: string,
  service?: CheckoutPaymentMethodService,
  typeId?: number,
  typeName?: string,
  vatRate?: number,
}

export type CheckoutApiResponse = {
  basket: CartApiResponse,
  paymentMethods: [],
  deliveryMethods: [],
  payments: [],
  shippingAddress: CheckoutAddressDetails
  billingAddress: CheckoutAddressDetails,
  payerAddress: CheckoutAddressDetails,
  buyerCompanyName: string|null,
}

/**
 * If changes, also fix emptyUser below
 */
export type UserProfile = {
  id: number,
  code: number|string;
  email: string,
  ssn: null,
  firstName: string,
  lastName: string,
  phone: string,
  impersonatingCompany: boolean,
  hasSeenOnboarding?: boolean,
  role: {
    id: number,
    name: string,
    type: NorceUserTypes.Admin | NorceUserTypes.User | NorceUserTypes.Finance | NorceUserTypes.Sales | NorceUserTypes.CustomerSuccess | NorceUserTypes.CountryManager | '', // user
  },
  company: Company,
  favoriteProducts: { id: string, quantity: number }[],
  favoriteBrands: string[],
  allowPointsPurchase?: boolean,
  currentlyEditingOrderProposal?: OrderProposal,
  currentlyEditingSampleRequest?: SampleRequest,
  savedBasketId?: string,
  pricelistIds?: number[],
  fixedPricelistIds?: number[],
}

/**
 * This should match the type above
 */
export const emptyUser: UserProfile = {
  id: -1,
  code: '',
  email: '',
  ssn: null,
  firstName: '',
  lastName: '',
  phone: '',
  impersonatingCompany: false,
  role: {
    id: -1,
    name: '',
    type: '', // user
  },
  company: {
    id: -1,
    code: '-1',
    name: '',
    orgNo: '',
    email: '',
    useInvoiceAddressAsDeliveryAddress: true,
    deliveryAddresses: [],
    invoiceAddress: {
      id: -1,
      line1: '',
      line2: '',
      zip: '',
      city: '',
      country: '',
      careOf: '',
      state: '',
    },
    phone: null,
    invoicePaymentMethodAvailable: false,
    customerGroups: [],
  },
  favoriteProducts: [],
  favoriteBrands: [],
  pricelistIds: [],
  fixedPricelistIds: [],
};

export type UserProfileAddress = {
  id: number,
  line1: string,
  line2: string,
  zip: string,
  city: string,
  country: string,
  careOf: string,
  state: string,
}

export const emptyUserAddress: UserProfileAddress = {
  id: -1,
  line1: '',
  line2: '',
  zip: '',
  city: '',
  country: '',
  careOf: '',
  state: '',
} as UserProfileAddress;

export type UsersMeCompanyPayload = {
  invoiceAddress?: {
    line1: string,
    line2: string,
    zip: string,
    city: string,
    country: string,
    careOf: string,
  },
  deliveryAddresses?: {
    line1: string,
    line2: string,
    zip: string,
    city: string,
    country: string,
    careOf: string,
  }[],
  email?: string,
  useInvoiceAddressAsDeliveryAddress: boolean,
}

/**
 * @deprecated
 */
export type UserProfileUpdatePayload = {
  id?: number,
  email?: string,
  ssn?: null,
  firstName?: string,
  lastName?: string,
  phone?: string,
  address?: {
    id?: string,
    street?: string,
    zip?: string,
    city?: string,
    country?: string
  },
  password?: string;
  company?: {
    id?: string,
    name?: string,
    orgNo?: string,
    address?: {
      id?: string,
      street?: string,
      zip?: string,
      city?: string,
      country?: string
    },
  }
}

export type OrdersApiResult = {
  itemCount: number,
  items: Order[],
  totalOrderCount?: number
}

export type SalesOrdersApiResult = {
  itemCount: number,
  items: SalesOrder[],
  page: number,
  lastPage: number,
  perPage: number,
}

export type SalesOrder = {
  id: string,
  orderNo: string,
  status: 'CREATED' | 'ALLOCATION' | 'CONFIRMED' | 'BACK ORDER' | 'DELIVERED' | 'INVOICED' | 'CANCELLED' | 'CREDIT CONTROL' | 'PARTLY DELIVERED' | 'ACKNOWLEDGED' | 'ERPCONFIRMED' | 'READYFORPICKUP' | 'PICKEDUP' | 'NOTPICKEDUP' | 'ONHOLD' | 'WAITINGFORCANCEL' | 'WAITINGFORONHOLDREMOVED' | 'CANCELERROR' | 'ACKNOWLEDGEMENTERROR' | 'READYFORRESERVATION' | 'RESERVED' | 'ABORTED',
  companyId: number,
  companyCode: string,
  companyName: string,
  totalSum: string,
  productCount: number,
  orderDate: string,
}

export type InvoicesApiResult = {
  invoices: Invoice[];
  total: number;
}

export type OrderProduct = {
  articleNr: string,
  brand: Brand,
  categories: Category[],
  colorInfo: ColorInfo,
  image: string,
  name: string,
  partNo: string,
  priceInfo: PriceInfo & {
    totalPrice: string,
    totalPriceDisplay: string,
    totalPriceBeforeDiscount: string,
    totalPriceBeforeDiscountDisplay: string,
    totalPriceBeforeVat: string,
    totalPriceBeforeVatDisplay: string,
  }
  quantity: number,
  url: string,
  variantName: string,
  info?: {code: string, value: string}[],
  dataLayer: ProductDataLayer,
  uom?: string,
  shipDate?: string,
}

export type Order = {
  orderStatus: 'CREATED' | 'ALLOCATION' | 'CONFIRMED' | 'BACK ORDER' | 'DELIVERED' | 'INVOICED' | 'CANCELLED' | 'CREDIT CONTROL' | 'PARTLY DELIVERED' | 'ACKNOWLEDGED' | 'ERPCONFIRMED' | 'READYFORPICKUP' | 'PICKEDUP' | 'NOTPICKEDUP' | 'ONHOLD' | 'WAITINGFORCANCEL' | 'WAITINGFORONHOLDREMOVED' | 'CANCELERROR' | 'ACKNOWLEDGEMENTERROR' | 'READYFORRESERVATION' | 'RESERVED' | 'ABORTED',
  estimatedDeliveryDate: string,
  deliveredDate: string | null,
  orderDate: string,
  norceOrderNo: string,
  orderNo: string,
  id: string,
  companyName: string;
  companyCode: string;
  shippingPrice: string,
  shippingPriceDisplay: string,
  shippingPriceBeforeVat: string,
  shippingPriceBeforeVatDisplay: string,
  additionalFees?: CartApiSummaryAdditionalFee[];
  crossDockingShipping?: string,
  crossDockingShippingDisplay?: string,
  crossDockingShippingBeforeVat?: string,
  crossDockingShippingBeforeVatDisplay?: string,
  totalOrderDiscount: string,
  totalOrderDiscountDisplay: string,
  totalOrderPrice: string,
  totalOrderPriceBeforeDiscount: string,
  totalOrderPriceBeforeDiscountBeforeVat: string,
  totalOrderPriceBeforeDiscountBeforeVatDisplay: string,
  totalOrderPriceBeforeDiscountDisplay: string,
  totalOrderPriceBeforeVat: string,
  totalOrderPriceBeforeVatDisplay: string,
  totalOrderPriceDisplay: string,
  products: OrderProduct[],
  info?: OrderInfoType[],
  parcelNumber: string | null, // will be entire link to tracking
  trackingLink?: string,
  poNumber?: string,
  orderedBy: string,
  points?: string,
  canBeCancelled?: boolean,
}

type OrderInfoType = {
  name: string,
  code: string,
  value?: string
  description?: string
};

export type Invoice = {
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string;
  totalSum: number;
  totalSumDisplay: string;
  voucherType: number;
  status: string;
  isOverdue: boolean;
}

export type OrderProposalPaginatedResult = {
  data: OrderProposal[],
  currentPage: number,
  perPage: number,
  lastPage: number,
  total: number,
  path: string,
}

export type OrderProposal = {
  id: number,
  customerId?: number,
  companyId: number,
  companyCode?: string,
  companyName: string,
  salesRepName: string,
  salesRepComment?: string,
  basketId: string,
  basket?: CartApiResponse,
  norceBasketId: number,
  norceCustomerBasketId?: number,
  customerBasketId?: string,
  originalRequestBasketId?: string,
  totalSum?: string,
  status: 'sent' | 'draft' | 'order' | 'declined' | 'expired' | 'partlyOrdered' | 'requestWaitingForSeller',
  seen?: string,
  source?: string,
  userComment?: string
  type?: string,
  declinedMessage?: string,
  validUntil: string,
  validWeeks?: number,
  createdAt: string,
  updatedAt: string,
}

export type SampleRequestStatus = 'draft' | 'awaiting_seller' | 'awaiting_country' | 'rejected_seller' | 'rejected_country' | 'order';

export type SampleRequestDeliveryAddress = {
  name?: string,
  addressLine?: string,
  postalCode?: string,
  city?: string,
}

export type SampleRequest = {
  id: number,
  srNumber: string,
  creatorType: 'customer' | 'sales_rep',
  isCreator: boolean,
  customerId?: number,
  companyId?: number,
  companyCode?: string,
  companyName?: string,
  basketId?: string,
  basket?: CartApiResponse,
  norceBasketId: number,
  storeAccountId?: string,
  status: SampleRequestStatus,
  wasRejected: boolean,
  sellerId?: number,
  sellerName?: string,
  sellerMessage?: string,
  countryManagerId?: number,
  countryManagerName?: string,
  countryManagerMessage?: string,
  orderNo?: string,
  totalSum?: string,
  itemCount?: number,
  deliveryType?: 'store' | 'address',
  deliveryAddress?: SampleRequestDeliveryAddress,
  reference?: string,
  deliveryInstruction?: string,
  contactUserId?: number,
  contactUserName?: string,
  contactEmail?: string,
  contactPhone?: string,
  notificationChannel?: 'email' | 'sms' | 'both',
  labelOrientation?: 'standing' | 'lying',
  labelPlacement?: string,
  createdAt: string,
  updatedAt: string,
}

export type SampleRequestPagedList = {
  data: SampleRequest[],
  currentPage: number,
  perPage: number,
  lastPage: number,
  total: number,
  path: string,
  counts?: { mine: number, other: number, order: number, rejected: number, all: number },
}

export type PriceHistoryItem = {
  date: string;
  price: string;
  priceDisplay: string;
  priceBeforeVatDisplay: string;
}
export type PriceHistory = {
  prices: PriceHistoryItem[]
}

export type UserCommunicationPreferences = {
  newsletter: boolean,
  backorderStockAlerts: boolean,
};

export type UserNotification = {
  type: string,
  id: string,
  seen: boolean,
  external_id?: string|number|null,
};

export type PurchaseResponse = {
  basketId: string,
  allowPointsPurchase: boolean,
  delayedUntil?: string|null,
}

export type Company = {
  id: number,
  name: string,
  code: string,
  orgNo: string | null,
  email: string,
  phone: string | null,
  useInvoiceAddressAsDeliveryAddress: boolean,
  deliveryAddresses: UserProfileAddress[],
  invoiceAddress: UserProfileAddress,
  invoicePaymentMethodAvailable: boolean,
  customerGroups: string[],
  excludedBrands?: string[],
  mod?: string,
  flags?: { name: string }[],
}

export type CompanyUser = {
  id: number,
  name: string,
  email: string,
}

export type CustomerSuccessProduct = {
  name: string;
  partNo: string;
  image: string | null;
}

export type TrackingLink = {
  orderNo: string;
  trackingLink: string;
}

export type ProductBackorderNotice = {
  customerNo: number;
  orderNo: number;
  lineNo: number;
  productNo: string;
  description: string;
  createdDate: number;
  quantity: number;
  estimatedDeliveryDate: string;
}

export type SavedCartsApiResponse = {
  carts: SavedCart[];
  page: number,
  perPage: number,
  totalCarts: number,
}
export type SavedCart = {
  id: number;
  uuid: string;
  companyId?: number;
  companyName?: string;
  creatorName?: string;
  createdByImpersonator?: boolean;
  createdByMe?: boolean;
  productCount?: number;
  totalSum?: string;
  createdAt: string,
  updatedAt?: string,
}

// I'm not a 100% sure this is from norce, but lets just pretend
export type FutureDeliveries =
{
  date: string | false,
  quantity: number,
  inStockNow: boolean,
  totalAvailable: number,
}

export type DiscountQuantityBreakApiResponse = {
  partNo: string,
  quantity: number,
  price: string,
  priceDisplay: string,
  priceBeforeVat: string,
  priceBeforeVatDisplay: string,
  discountPercentage: number,
}

export type DiscountQuantityBreak = DiscountQuantityBreakApiResponse & {
  reached?: boolean,
  areaPrice?: number,
  unitDisplay: string, // example m²
  areaPerPackage: number,
}

export type ProductFilesDetails = {
  id: number,
  type: number,
  path: string | null,
  name: string,
  description: string,
  key: string,
  extension: string,
  code: string,
  sortOrder: string,
  imageAltText: string
}

export type CategoryDetails = {
  code: string,
  name: string,
  description: string,
  imagePath: string | null
}

export type ClaimItem = {
  ticketNumber: string,
  title?: string,
  product?: string,
  productNumber?: string,
  productName?: string,
  stage: string,
  status: string,
  createdDate: string,
  closedDate: string
}

export type RetailersApiItem = {
  accountNumber: string;
  accountid: string;
  addressLine1: string;
  addressLine2: string;
  bookAppointment: boolean;
  city: string;
  country: string;
  description: string;
  email: string | null,
  exibitionRating: number,
  kahrs: boolean,
  karelia: boolean,
  laminate: boolean,
  inventoryDate?: string | null, // set when the store has been inventoried (Workato: InventoryDate)
  samplesCount?: number | null, // number of display samples at the last inventory (Workato: SamplesCount)
  latitude: number,
  longitude: number,
  name: string;
  postalcode: string;
  resilient: boolean,
  showonweb: boolean,
  phone: string | null,
  textile: boolean,
  website: string;
  wood: boolean,
}
export type RetailersApiResponse = {
  stores: RetailersApiItem[]
}

// /retailers/display-samples: shopAccountId (same as accountid in /retailers) of the stores that have each product on display
export type DisplaySamplesResponse = {
  refreshedAt: string | null,
  displaySamples: Record<string, string[]>,
}

// Expanded model for retailers
export type StoreModel = RetailersApiItem & {
  id: string; // not from api, added by loading
  exhibition: string, // not from api, added by exibitionRating
  distance?: number; // not from api
  floors: string; // not from api
  address: string; // not from api
}

// Google Geocoding API response types
export type GeocodeAddressComponent = {
  long_name: string;
  short_name: string;
  types: string[];
}

export type GeocodeLocation = {
  lat: number;
  lng: number;
}

export type GeocodeGeometry = {
  location: GeocodeLocation;
  location_type: string;
  viewport: {
    northeast: GeocodeLocation;
    southwest: GeocodeLocation;
  };
}

export type GeocodeResult = {
  address_components: GeocodeAddressComponent[];
  formatted_address: string;
  geometry: GeocodeGeometry;
  place_id: string;
  types: string[];
}

export type GeocodeApiResponse = {
  results: GeocodeResult[];
  status: string;
}

// Norce backend geocode response (flattened version of Google Geocoding response)
export type GeocodeResponse = {
  lat: number;
  lng: number;
  formatted_address: string;
  city: string;
  location_type: string;
  types: string[];
  place_id: string;
  partial_match: boolean;
  plus_code: string | null;
}

// Sample Inventory types
export type SampleCountStatus = 'NOT_COUNTED' | 'COUNTED' | 'MISSING';

export type SampleInventoryItem = {
  partNo: string;
  estimatedPresent: number;
  inventoriedQty: number;
  countStatus: SampleCountStatus;
};

export type SampleInventoryStore = {
  shopName: string;
  shopAccountId: string;
  shopAccountNumber: string;
  lastCountedAt?: string | null;
  inventory: SampleInventoryItem[];
};

export type SampleInventoryPricelistRow = {
  partNo: string;
  name: string;
  collection: string;
  price: number | null;
};

// Only affects the printed/downloaded price list, nothing is saved
export type SampleInventoryPricelistPricing = {
  mode: 'discount' | 'custom';
  discount?: number;
  overrides?: Record<string, number>;
  prices?: Record<string, number>;
};

export type SampleInventoryApiResponse = {
  stores: SampleInventoryStore[];
};

export type SampleInventoryUpdatePayload = {
  InventoryUpdate: {
    CustomerId: string;
    DataAreaId: string;
    CountedAt: string;
    CountedBy: string;
    stores: {
      ShopName: string;
      ShopAccountNumber: string;
      ShopAccountId: string;
      Inventory: {
        PartNo: string;
        InventoriedQty: number;
      }[];
    }[];
  };
};
